import React, { useContext, useState } from 'react';
import { TextField } from '@mui/material';
import { FormStyles } from '../../config/Theme';
import {LanguageProp} from "../../config/LanguageConfig";
import {LanguageContext} from "../../config/ContextConfig";
import {InputState} from "../../models/common/Form";
import {makeStyles} from "@mui/styles";

/**
 * Represents a function called when a text input value changes.
 *
 * This type is used to handle change events for text-based inputs, where the application
 * needs to capture and respond to updates in user-entered data.
 *
 * @param inputName - The name or identifier of the input field that triggered the change event.
 *                    This allows distinguishing between multiple inputs.
 * @param value - The new value entered in the input field after the change event occurred.
 */
export type TextInputChangeFunc = ( inputName: string, value: string ) => void

/**
 * Represents the properties for a configurable text input component.
 * This interface is designed to manage user input fields in a flexible way,
 * while supporting validation, error handling, and customization for both single
 * and multi-line inputs.
 */
export interface TextInputProps extends Partial<LanguageProp> {
    /** only use this change function, when you need the complete event. otherwise use onTextChange */
    onChange?: ( event: React.ChangeEvent< HTMLInputElement > ) => void
    onTextChange?: TextInputChangeFunc
    baseValue?: string
    label?: string
    required?: boolean
    value?: string
    helperText?: string
    hasError?: boolean
    errorText?: string
    name?: string
    isValid?( value: string): boolean
    onValidationFinished?: ( input: InputState<string> ) => void
    onBlur?: ( value: string ) => void
    type?: string
    ref?: React.Ref<any>
    inputRef?: React.Ref<any>
    multiline?: boolean
    rows?: number
    rowsMax?: number
    disabled?: boolean
}

/**
 * Provides a set of reusable and consistent styling rules for components.
 *
 * This variable uses the `makeStyles` function to combine a base theme with form-specific styles,
 * ensuring a consistent design and user interface across components.
 *
 * The merged styles account for both global and specific form design elements, allowing easy
 * customization and alignment with the app's design system. This is especially useful for rapid
 * development and maintaining consistency when working with forms.
 *
 * @param theme The application's theme object that provides access to global design tokens
 * (e.g., colors, spacing, typography).
 * @return An object containing CSS-in-JS style rules that can be applied to components.
 * @throws An error if the theme object is undefined or improperly structured.
 */
const useStyles = makeStyles( ( theme ) => ({
    ...FormStyles( theme )
}) );

/**
 * Renders an interactive text input field with validation capabilities.
 * Used for capturing user input with optional validation, feedback, and customization.
 *
 * This component supports advanced features such as validation hooks, error handling,
 * and dynamic helper text, enabling richer user input experiences with minimal setup.
 *
 * Wrapper around the mui `TextField` component.
 *
 * @param props The configuration object for the `TextInput` component.
 * @param props.name The unique name identifier for the input field. Defaults to "generic-name-" if unspecified.
 * @param props.value The current value of the text input.
 * @param props.label The label to display above the input field.
 * @param props.helperText Helper text displayed below the input field. Defaults to an empty string if not specified.
 * @param props.errorText Text to display when an error is present.
 * @param props.hasError Indicates whether the input field should display an error state. Overrides internal validation if set.
 * @param props.required Specifies whether the input field is mandatory. Defaults to `false` if not explicitly provided.
 * @param props.disabled Disables the input field, preventing user interaction. Defaults to `false`.
 * @param props.multiline Specifies whether the input field should allow multiple lines of text. Defaults to `false`.
 * @param props.rows Minimum number of rows for the input if multiline is enabled. Defaults to `1`.
 * @param props.rowsMax Maximum number of rows for the input if multiline is enabled.
 * @param props.type The type of the input field (e.g., "text", "number", "email"). Defaults to "text".
 * @param props.inputRef A reference to the underlying input element for advanced use cases.
 * @param props.ref A reference used for managing the component itself.
 * @param props.isValid A custom function for validating the text input. Returns `true` or `false` based on validity.
 * @param props.onTextChange Callback invoked when the text input value changes. Receives the name and value as arguments.
 * @param props.onBlur Callback invoked when the input loses focus. Receives the current value as an argument.
 * @param props.onValidationFinished Callback triggered after validation is completed. Receives the validation state and input metadata.
 *
 * @return A fully configured `TextField` component that includes all user-provided and default behaviors.
 *
 * @throws {Error} Throws internal validation errors when invalid `props` are supplied or if required fields are missing input.
 */
function TextInput( props: TextInputProps ) {
    //declare react state hook variables
    const [ helperText, setHelperText ] = useState( props.helperText || "" );
    const [ error, setError ] = useState( {
        hasError: false,
        errorText: ""
    });
    const classes = useStyles();

    // set context vars
    const language = useContext( LanguageContext );

    const onBlur = ( value: string ) => {
        baseValidator( value );
        if( typeof props.onBlur !== "undefined") {
            props.onBlur( value )
        }
    };

    const baseValidator = ( value: string ) => {
        let isValidated = true;
        let errorMsg = props.errorText || "";
        const inputState = {
            inputName: props.name || "generic-name-",
            validation: error,
            value: value,
            isRequired: props.required || false,
            isDisabled: props.disabled || false
        };

        //if isValid function is set, use this function to validate the TextInput, when not set use standard function
        if( typeof props.isValid !== 'undefined' ) {
            isValidated = props.isValid( value );
        } 

        //check if required input is filled
        if( props.required && value === "" ) {
            isValidated = false;
            errorMsg = language.errors.form.requiredFieldNotFilled.errorText
        }

        let validationResult = { hasError: false, errorText: "" }
        if( isValidated ) {
            setError( validationResult);
            setHelperText( props.helperText || ""  );
        } else {
            setHelperText( props.helperText || "" );
            validationResult = { hasError: true, errorText: errorMsg };
            setError( error );
        }

        //when the onValidationSuccess prop is set, call the callback function
        if( props.onValidationFinished ) {
            props.onValidationFinished( {
                ...inputState,
                validation: validationResult
            });
        }

    };

    const onChange = (event: React.ChangeEvent< HTMLInputElement >) => {
        const element = event.target;
        baseValidator( element.value );
        if( typeof props.onTextChange !== "undefined" ) {
            props.onTextChange(element.name, element.value);
        }
    };

    return (
        <TextField 
            className={ classes.input}
            inputRef={ props.inputRef }
            ref={ props.ref }
            label={ props.label }
            required={ props.required }
            variant="outlined"
            helperText={ ( props.hasError || error.hasError) ? props.errorText || error.errorText : helperText  }
            error={ ( props.hasError || error.hasError )}
            value={props.value}
            onBlur={ () => onBlur( props.value ?? "" )}
            onChange={onChange}
            name={ props.name }
            type={props.type ?? "text"}
            multiline={ props.multiline ?? false }
            minRows={ props.rows ?? 1 }
            maxRows={ props.rowsMax ?? 1 }
            disabled={ props.disabled }
        />
    );
}

export default TextInput;